// @ts-nocheck — legacy in-house cart script with untyped DOM access throughout. The repo has no
// committed jsconfig/tsconfig; these were editor-only `checkJs` diagnostics, not runtime bugs or CI
// failures. Matches the existing precedent in assets/popover-polyfill.js. New cart code is fully
// typed (see assets/cart-drawer-discount.js); typing this file is a separate, dedicated refactor.

/* ---- CUSTOM Jump to Section ---- */
document.addEventListener('click', (e) => {
  const trigger = e.target.closest('[data-jump-to-section-id]');
  if (!trigger) return;

  const id = trigger.dataset.jumpToSectionId;

  const target = document.querySelector(
    `[data-anchor-section-id="${id}"]`
  );

  target?.scrollIntoView({ behavior: 'smooth' });
});

/* ---- PDP custom ebook atc ---- */

async function customAddToCartHandler(event) {
  const button = event.target.closest('.custom-add-to-cart-button');
  if (!button) return;

  event.preventDefault();
  event.stopImmediatePropagation();

  const wrapper = document.querySelector('.additional-tests__item-wrapper');
  if (!wrapper) return;

  const uniqueIds = [
    ...new Set(
      Array.from(wrapper.querySelectorAll('.additional-test__checkbox'))
        .filter(cb => cb.checked && cb.dataset.productid)
        .map(cb => Number(cb.dataset.productid))
    ),
  ];

  if (!uniqueIds.length) return;

  if (button.dataset.processing === 'true') return;
  button.dataset.processing = 'true';

  try {
    await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: uniqueIds.map(id => ({
          id,
          quantity: 1,
          properties: {
            _timestamp: Date.now(),
          }
        })),
      }),
    })
      .then((response) => response.json())
      .then(async () => {
        const updatedCart = await fetch("/cart.js").then((r) =>
          r.json()
        );
        document.dispatchEvent(
          new CustomEvent("cart:update", {
            bubbles: true,
            detail: {
              resource: updatedCart,
              sourceId: "eBooks",
              data: {
                source: "eBooks",
                itemCount: updatedCart.item_count,
              },
            },
          })
        );
      });

  } catch (e) {
    console.error('[customAddToCart] failed', e);
  } finally {
    button.dataset.processing = 'false';
  }
}

document.removeEventListener('click', customAddToCartHandler, true);
document.addEventListener('click', customAddToCartHandler, true);

/* ------------------------------------------------------------------------------------------------------------- */
/* ----------------------------------------           CART       ----------------------------------------------- */
/* ------------------------------------------------------------------------------------------------------------- */

// Helper function to dispatch cart update event
function dispatchCartUpdate(updatedCart) {
  document.dispatchEvent(
    new CustomEvent("cart:update", {
      bubbles: true,
      detail: {
        resource: updatedCart,
        sourceId: "cart__additional-test-checkbox",
        data: {
          source: "cart__additional-test-checkbox",
          itemCount: updatedCart.item_count,
          // When the caller requested rendered sections, hand them to cart-items-component so it
          // morphs from this response instead of firing a second, uncached section render.
          sections: updatedCart.sections,
        },
      },
    })
  );
}

async function cartWrite(endpoint, payload) {
  const response = await fetch(endpoint, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(`${endpoint} ${response.status}: ${data.description || data.message || "cart write failed"}`);
  }
  return data;
}

/**
 *
 * @param {() => Promise<unknown>} op
 */
let cartOpQueue = Promise.resolve();
let pendingCartOps = 0;

function enqueueCartOp(op) {
  pendingCartOps += 1;

  cartOpQueue = cartOpQueue
    .then(() => op())
    .then(async () => {
      if (pendingCartOps !== 1) return; // not the last op in the burst yet
      const cart = await fetch("/cart.js").then((r) => r.json());
      dispatchCartUpdate(cart);
    })
    .catch(async (error) => {
      console.error("Cart operation failed:", error);
      if (pendingCartOps !== 1) return;
      try {
        const cart = await fetch("/cart.js").then((r) => r.json());
        dispatchCartUpdate(cart);
      } catch (_) {}
    })
    .finally(() => {
      pendingCartOps -= 1;
    });

  return cartOpQueue;
}

/* ------------------------------------------------------------------------------------------------------------- */
/* ----------------------------------------   BUNDLE QUANTITY SELECTOR   --------------------------------------- */
/* ------------------------------------------------------------------------------------------------------------- */

/**
 * Shared cart quantity logic extracted into standalone functions so both the
 * web component (issue 3) and the dropdown handler (which lives on a plain
 * <select>, not a custom element) can call the same code paths.
 *
 * Both helpers accept an AbortSignal so the caller can cancel in-flight
 * requests when a new interaction arrives (issue 2).
 */

/**
 * Fetches the current _source AB-test value from the cart.
 * @param {AbortSignal} signal
 * @returns {Promise<string|undefined>}
 */
async function getAbTestSource(signal) {
  const cart = await fetch('/cart.js', { signal }).then(r => r.json());
  return cart.items.find(item => item.properties?._source)?.properties?._source;
}

/**
 * Adds `difference` copies of a product to the cart, each with a unique
 * _main_product_key and a descending _timestamp so ordering is deterministic.
 *
 * @param {{ productId: string, difference: number, signal: AbortSignal }} opts
 * @returns {Promise<object>} Updated cart from /cart/add.js
 */
async function cartBundleIncrease({ productId, difference, signal }) {
  const now = Date.now();
  const abTestSource = await getAbTestSource(signal);

  return fetch('/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    signal,
    body: JSON.stringify({
      items: Array.from({ length: difference }, (_, index) => ({
        id: productId,
        quantity: 1,
        properties: {
          _main_product_key: crypto.randomUUID(),
          _timestamp: now - index,
          ...(abTestSource && { _source: abTestSource }),
        },
      })),
    }),
  }).then(r => r.json());
}

/**
 * Removes the `difference` newest cart lines for a given productId.
 * "Newest" is determined by the _timestamp property on each line.
 *
 * @param {{ productId: string, difference: number, signal: AbortSignal }} opts
 * @returns {Promise<object>} Updated cart from /cart/update.js
 */
async function cartBundleDecrease({ productId, difference, signal }) {
  const cart = await fetch('/cart.js', { signal }).then(r => r.json());

  const matchingItems = cart.items.filter(
    item => Number(item.id) === Number(productId)
  );

  const itemsToRemove = matchingItems
    .sort((a, b) => {
      const aTs = Number(a.properties?._timestamp || 0);
      const bTs = Number(b.properties?._timestamp || 0);
      return bTs - aTs; // DESC — newest first
    })
    .slice(0, difference);

  const updates = {};
  itemsToRemove.forEach(item => { updates[item.key] = 0; });

  return fetch('/cart/update.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    signal,
    body: JSON.stringify({ updates }),
  }).then(r => r.json());
}

/* --- BundleQuantitySelectorComponent ----------------------------------------
 *
 * Replaces the four document-level click/change listeners that previously
 * handled .quantity-minus / .quantity-plus / input[type=number] inside
 * quantity-selector-component.custom-quantity-selector-bundle.
 *
 * Usage in Liquid: swap the outer tag from
 *   <quantity-selector-component class="custom-quantity-selector-bundle" …>
 * to
 *   <bundle-quantity-selector …>
 *
 * All data-* attributes stay exactly the same:
 *   data-productid, data-mainproductkey, data-currentqty
 * All inner markup stays exactly the same.
 *
 * The component owns its own AbortController, so rapid +/- clicks cancel the
 * previous in-flight request before starting a new one (fixes issue 2).
 * The theme's on:click="/decreaseQuantity" never fires because events are
 * handled at component level, not delegated from document (fixes issue 3 /
 * partially addresses issue 1 for the bundle case).
 * -------------------------------------------------------------------------- */

class BundleQuantitySelectorComponent extends HTMLElement {
  /** @type {AbortController|null} */
  #controller = null;

  connectedCallback() {
    this.addEventListener('click', this.#handleClick);
    this.addEventListener('change', this.#handleChange);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('change', this.#handleChange);
    this.#controller?.abort();
  }

  // ── Event handlers ──────────────────────────────────────────────────────

  #handleClick = (e) => {
    const button = e.target.closest('.quantity-minus, .quantity-plus');
    if (!button) return;

    // Prevent the theme's declarative on:click="/decreaseQuantity" from also
    // firing — our component owns this interaction entirely.
    e.stopImmediatePropagation();

    const input = this.querySelector('input[type="number"]');
    if (!input) return;

    const oldValue = Number(this.dataset.currentqty);
    const step     = parseInt(input.getAttribute('step'), 10) || 1;
    const max      = input.getAttribute('max') ? parseInt(input.getAttribute('max'), 10) : Infinity;

    let newQuantity;
    if (button.classList.contains('quantity-plus')) {
      newQuantity = Math.min(oldValue + step, max);
    } else {
      newQuantity = Math.max(oldValue - step, 0);
    }

    if (newQuantity === oldValue) return;

    // Optimistic UI update
    input.value = newQuantity;
    this.dataset.currentqty = newQuantity;

    this.#syncCart(oldValue, newQuantity);
  };

  #handleChange = (e) => {
    const input = e.target;
    if (input.type !== 'number') return;

    const step = parseInt(input.getAttribute('step'), 10) || 1;
    const max  = input.getAttribute('max') ? parseInt(input.getAttribute('max'), 10) : Infinity;

    const oldValue = Number(this.dataset.currentqty);
    let newQuantity = parseInt(input.value, 10) || 0;

    // Clamp + snap to step
    newQuantity = Math.max(0, Math.min(newQuantity, max));
    newQuantity = Math.round(newQuantity / step) * step;

    if (newQuantity === oldValue) {
      input.value = oldValue;
      return;
    }

    input.value = newQuantity;
    this.dataset.currentqty = newQuantity;

    this.#syncCart(oldValue, newQuantity);
  };

  // ── Core cart sync ───────────────────────────────────────────────────────

  /**
   * Cancels any in-flight request for this component instance, then runs the
   * appropriate add/remove cart operation.
   *
   * @param {number} oldValue
   * @param {number} newQuantity
   */
  async #syncCart(oldValue, newQuantity) {
    // Abort previous in-flight request (issue 2 fix)
    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    const productId  = this.dataset.productid;
    const difference = Math.abs(newQuantity - oldValue);
    const isIncrease = newQuantity > oldValue;

    try {
      const updatedCart = isIncrease
        ? await cartBundleIncrease({ productId, difference, signal })
        : await cartBundleDecrease({ productId, difference, signal });

      dispatchCartUpdate(updatedCart);
    } catch (err) {
      if (err.name === 'AbortError') return; // superseded by a newer click, silently discard
      console.error('[BundleQty] cart sync failed', err);
    } finally {
      this.#controller = null;
    }
  }
}

if (!customElements.get('bundle-quantity-selector')) {
  customElements.define('bundle-quantity-selector', BundleQuantitySelectorComponent);
}

/* --- Cart quantity Dropdown (Main product in Bundle) -----------------------
 *
 * The dropdown lives on a plain <select class="custom__quantity-dropdown">,
 * not a custom element, so it stays as a delegated listener on document.
 * It now shares cartBundleIncrease / cartBundleDecrease and uses its own
 * AbortController map keyed by productId (issue 2 fix).
 * -------------------------------------------------------------------------- */

/** @type {Map<string, AbortController>} */
const dropdownControllers = new Map();

document.addEventListener('change', async function (e) {
  if (!e.target.classList.contains('custom__quantity-dropdown')) return;

  const dropdown = e.target;
  const newQuantity = Number(dropdown.value);
  const oldValue    = Number(dropdown.dataset.currentqty);
  const productId   = dropdown.dataset.productid;

  if (newQuantity === oldValue) return;

  dropdown.dataset.currentqty = newQuantity;

  // Cancel previous in-flight request for this product (issue 2 fix)
  dropdownControllers.get(productId)?.abort();
  const controller = new AbortController();
  dropdownControllers.set(productId, controller);
  const { signal } = controller;

  const difference = Math.abs(newQuantity - oldValue);
  const isIncrease = newQuantity > oldValue;

  try {
    const updatedCart = isIncrease
      ? await cartBundleIncrease({ productId, difference, signal })
      : await cartBundleDecrease({ productId, difference, signal });

    dispatchCartUpdate(updatedCart);
  } catch (err) {
    if (err.name === 'AbortError') return;
    console.error('[BundleDropdown] cart sync failed', err);
  } finally {
    dropdownControllers.delete(productId);
  }
});

/* --- Cart additional-test checkbox ---------------------------------------- */

document.addEventListener("change", (event) => {
  const checkbox = event.target;
  if (!checkbox.matches('[id^="cart__additional-test-checkbox"]')) return;
  if (!checkbox.closest(".cart-items__table-row")) return;
  scheduleAddonReconcile();
});

const ADDON_RECONCILE_DELAY = 1000;
let addonReconcileTimer = null;

function scheduleAddonReconcile() {
  setCartUpdating(true); // instant feedback the moment a box is toggled
  if (addonReconcileTimer) clearTimeout(addonReconcileTimer);
  addonReconcileTimer = setTimeout(() => {
    addonReconcileTimer = null;
    enqueueCartOp(reconcileAddons);
  }, ADDON_RECONCILE_DELAY);
}

/**
 * @returns {Map<string, Set<string>>} mainProductKey -> set of checked add-on variant ids
 */
function buildDesiredAddonsFromDOM() {
  const desired = new Map();
  document.querySelectorAll('[id^="cart__additional-test-checkbox"]').forEach((cb) => {
    const mainKey = cb.dataset.mainproductkey;
    const variantId = cb.dataset.productid;
    if (!mainKey || !variantId) return;
    if (!desired.has(mainKey)) desired.set(mainKey, new Set());
    if (cb.checked) desired.get(mainKey).add(String(variantId));
  });
  return desired;
}

/** @param {object} main - a cart line item @returns {Set<string>} its current add-on variant ids */
function bundleAddonVariantSet(main) {
  return new Set(JSON.parse(main.properties._bundle_addons || "[]").map((a) => String(a.variant_id)));
}

async function reconcileAddons() {
  const desired = buildDesiredAddonsFromDOM();
  const cart = await fetch("/cart.js").then((r) => r.json());

  const changed = [];
  for (const [mainKey, desiredSet] of desired) {
    const main = cart.items.find((i) => i.properties?._main_product_key === mainKey);
    if (!main) continue;
    const currentSet = bundleAddonVariantSet(main);
    const differs =
      [...desiredSet].some((v) => !currentSet.has(v)) || [...currentSet].some((v) => !desiredSet.has(v));
    if (differs) changed.push({ mainKey, desiredSet });
  }

  for (let i = 0; i < changed.length; i++) {
    const { mainKey, desiredSet } = changed[i];
    const freshCart = i === 0 ? cart : await fetch("/cart.js").then((r) => r.json());
    await syncPersonAddons(freshCart, mainKey, desiredSet);
  }
}

/** Where the add-on checkboxes currently live, so PostHog events are attributable. */
function cartAddonSource() {
  const checkbox = document.querySelector('[id^="cart__additional-test-checkbox"]');
  return checkbox && checkbox.closest(".cart-drawer") ? "cart_drawer" : "cart_page";
}

/**
 * @param {string} name - event name
 * @param {object} main - the main cart line item
 * @param {object} extra - additional event properties
 */
function trackAddonEvent(name, main, extra) {
  if (typeof window.phCapture !== "function") return;
  window.phCapture(name, {
    source: cartAddonSource(),
    product_id: main && main.product_id,
    product_title: main && main.product_title,
    ...extra,
  });
}

/**
 * @param {object} cart
 * @param {string} mainKey
 * @param {Set<string>} desiredSet
 */
async function syncPersonAddons(cart, mainKey, desiredSet) {
  const main = cart.items.find((i) => i.properties?._main_product_key === mainKey);
  if (!main) return;

  const currentSet = bundleAddonVariantSet(main);
  const toAdd = [...desiredSet].filter((v) => !currentSet.has(v));
  const toRemove = [...currentSet].filter((v) => !desiredSet.has(v));
  if (!toAdd.length && !toRemove.length) return;

  try {
    if (!toRemove.length) {
      // Pure additions — add the source lines; the Cart Transform merges them.
      await cartWrite("/cart/add.js", {
        items: toAdd.map((v) => ({ id: v, quantity: 1, properties: { _blongs_to_main_product_key: mainKey } })),
      });
    } else {
      // Any removal needs a rebuild: tear down the merged line, re-add main + the desired add-ons.
      const p = main.properties;
      await cartWrite("/cart/change.js", { id: main.key, quantity: 0 });
      await cartWrite("/cart/add.js", {
        items: [
          {
            id: p._main_variant_id,
            quantity: main.quantity || 1,
            properties: {
              _main_product: "true",
              _main_product_key: mainKey,
              _main_variant_id: p._main_variant_id,
              _main_product_price: p._main_product_price,
              _timestamp: p._timestamp,
              _main_product_index: p._main_product_index,
            },
          },
          ...[...desiredSet].map((v) => ({
            id: v,
            quantity: 1,
            properties: { _blongs_to_main_product_key: mainKey },
          })),
        ],
      });
    }
  } catch (error) {
    trackAddonEvent("addon_change_failed", main, {
      added: toAdd,
      removed: toRemove,
      error: String((error && error.message) || error),
    });
    throw error; // let the queue re-sync the UI to the true cart
  }

  trackAddonEvent("addon_changed", main, {
    added: toAdd,
    removed: toRemove,
    addon_count: desiredSet.size,
  });
}

async function flushCartOps() {
  if (addonReconcileTimer) {
    clearTimeout(addonReconcileTimer);
    addonReconcileTimer = null;
    enqueueCartOp(reconcileAddons);
  }
  // Flush a pending debounced removal batch and wait for the in-flight one, so checkout never
  // proceeds with an item the customer just removed.
  if (bundleRemovalTimer) {
    clearTimeout(bundleRemovalTimer);
    flushBundleRemovals();
  }
  await bundleRemovalChain;
  while (pendingCartOps > 0) {
    await cartOpQueue;
  }
}

let cartUpdatingWatchdog = null;

function setCartUpdating(isUpdating) {
  document.documentElement.classList.toggle("cart-updating", isUpdating);
  clearTimeout(cartUpdatingWatchdog);
  if (isUpdating) {
    cartUpdatingWatchdog = setTimeout(() => document.documentElement.classList.remove("cart-updating"), 10000);
  }
}

(function clearCartUpdatingOnRender() {
  const cartItems = document.querySelector("cart-items-component");
  if (!cartItems) return;
  new MutationObserver(() => {
    if (pendingCartOps === 0 && !addonReconcileTimer) setCartUpdating(false);
  }).observe(cartItems, { childList: true, subtree: true, characterData: true });
})();

// REMOVE BUNDLE PRODUCTS BY ID — rapid-removal safe.
// The Cart Transform re-keys every line on each mutation (CLAUDE.md §7), so firing a separate
// /cart/update.js per click races: a later removal reads keys an earlier one already invalidated
// and silently no-ops, while N concurrent Cart-Transform runs cause the lag. We collapse the row
// instantly for feedback, then coalesce a burst of clicks into a single debounced, SERIALIZED
// batch (one fresh cart read + one update per batch).
const pendingBundleRemovals = new Set();
let pendingBundleRows = [];
let bundleRemovalTimer = null;
let bundleRemovalChain = Promise.resolve();
const BUNDLE_REMOVAL_DEBOUNCE = 250;

document.addEventListener('click', function (e) {
  const button = e.target.closest('.cart-items__remove--custom-bundle');
  if (!button) return;

  const productId = button.dataset.productid;
  if (!productId) {
    console.error('No product ID found');
    return;
  }

  // Optimistic: collapse the clicked row immediately (reuses the .removing keyframe).
  const row = button.closest('.cart-items__table-row');
  if (row) {
    row.style.setProperty('--row-height', `${row.clientHeight}px`);
    row.classList.add('removing');
    pendingBundleRows.push(row);
  }

  pendingBundleRemovals.add(String(productId));
  clearTimeout(bundleRemovalTimer);
  bundleRemovalTimer = setTimeout(flushBundleRemovals, BUNDLE_REMOVAL_DEBOUNCE);
});

function flushBundleRemovals() {
  bundleRemovalTimer = null;
  if (!pendingBundleRemovals.size) return;

  const productIds = new Set([...pendingBundleRemovals].map(Number));
  const rows = pendingBundleRows;
  pendingBundleRemovals.clear();
  pendingBundleRows = [];

  // Serialize behind any in-flight batch so each batch reads fresh, non-stale line keys.
  bundleRemovalChain = bundleRemovalChain.then(() => removeBundleProducts(productIds, rows));
}

async function removeBundleProducts(productIds, rows) {
  try {
    const cart = await fetch('/cart.js').then((r) => r.json());

    const updates = {};
    cart.items.forEach((item) => {
      if (productIds.has(Number(item.product_id))) updates[item.key] = 0;
    });
    if (!Object.keys(updates).length) return;

    const sectionsToUpdate = Array.from(document.querySelectorAll('cart-items-component'))
      .map((el) => el.dataset.sectionId)
      .filter(Boolean);

    const updatedCart = await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        updates,
        sections: sectionsToUpdate,
        sections_url: window.location.pathname,
      }),
    }).then((response) => response.json());

    dispatchCartUpdate(updatedCart);
  } catch (error) {
    rows.forEach((row) => row.classList.remove('removing')); // restore rows if the batch failed
    console.error('Error removing items from cart:', error);
  }
}

// GO TO CHECKOUT
document.addEventListener("click", async (event) => {
  const checkoutButton = event.target.closest("#CartPage-Checkout");
  if (!checkoutButton) return;

  event.preventDefault();

  if (document.documentElement.classList.contains("cart-updating")) return;

  if (checkoutButton.dataset.processing === "true") return;
  checkoutButton.dataset.processing = "true";

  try {
    await flushCartOps();

    const cartResponse = await fetch("/cart.js");
    const cart = await cartResponse.json();

    const rows = document.querySelectorAll(".cart-items__test-contaner");

    for (const row of rows) {
      const itemKey = row.dataset.itemkey;
      if (!itemKey) continue;

      const nameInput = row.querySelector(".cart-items__name-input-field");
      if (!nameInput) continue;

      const inputValue = nameInput.value.trim();
      if (!inputValue) continue;

      const lineItem = cart.items.find(item => item.key === itemKey);
      if (!lineItem) continue;

      const mergedProperties = { ...(lineItem.properties || {}) };

      const rowIndex = nameInput.dataset.row || "1";
      mergedProperties[`_row_name_${rowIndex}`] = inputValue;

      await fetch("/cart/change.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id: lineItem.key,
          quantity: lineItem.quantity,
          properties: mergedProperties,
        }),
      });
    }

    await fixAddonQuantities();
    window.location.href = "/checkout";

  } catch (error) {
    console.error("Checkout preparation failed:", error);
    checkoutButton.dataset.processing = "false";
  }
});

// PreSave + Delete Test name
document.addEventListener("focusout", async (event) => {
  const input = event.target.closest(".cart-items__name-input-field");
  if (!input) return;

  await saveTestName(input);
});

document.addEventListener("keydown", (event) => {
  if (event.key !== "Enter") return;

  const input = event.target.closest(".cart-items__name-input-field");
  if (!input) return;

  event.preventDefault();
  input.blur();
});

// Per-person trash button: only CLEARS that person's assigned name field and persists the clearing —
// it must NOT remove the person's line items from the cart. saveTestName() writes an empty value back
// as null, so blanking the input and re-saving is the whole operation.
document.addEventListener("click", async (event) => {
  const removeBtn = event.target.closest(".cart-items__name-input-remove");
  if (!removeBtn) return;

  event.preventDefault();

  const row = removeBtn.closest(".cart-items__test-contaner");
  if (!row) return;

  const input = row.querySelector(".cart-items__name-input-field");
  if (!input) return;

  input.value = "";
  await saveTestName(input);
});

async function saveTestName(input) {
  const row = input.closest(".cart-items__test-contaner");
  if (!row) return;

  const itemKey = row.dataset.itemkey;
  if (!itemKey) return;

  const value = input.value.trim();

  const cartResponse = await fetch("/cart.js");
  const cart = await cartResponse.json();

  const lineItem = cart.items.find(item => item.key === itemKey);
  if (!lineItem) return;

  const propKey = `_row_name_1`;
  const newValue = value === "" ? null : value;

  if (lineItem.properties?.[propKey] === newValue) return;

  const mergedProperties = {
    ...(lineItem.properties || {}),
    [propKey]: newValue,
  };

  await fetch("/cart/change.js", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      id: lineItem.key,
      quantity: lineItem.quantity,
      properties: mergedProperties,
    }),
  });
}

// Listen for BFCache restore -> cart page bug
window.addEventListener('pageshow', function (event) {
  const navType = performance.getEntriesByType('navigation')[0]?.type;
  const cameFromCheckout = document.referrer.includes('checkout');
  const wentToCheckout = sessionStorage.getItem('wentToCheckout') === 'true';

  // Only proceed if user came from checkout (via referrer or flag)
  if (!cameFromCheckout && !wentToCheckout) return;

  if (navType === 'back_forward' || event.persisted) {
    sessionStorage.removeItem('wentToCheckout');
    // Force immediate reload - faster and no flash of stale content
    window.location.reload();
  }
});

/* ---- CUSTOM cart:update ---- */

document.addEventListener("DOMContentLoaded", () => {
  fixAddonQuantities();
});

async function fixAddonQuantities() {
  const CHECKBOX_SELECTOR = '[id^="cart__additional-test-checkbox"]';
  const checkboxes = document.querySelectorAll(CHECKBOX_SELECTOR);

  if (!checkboxes.length) return;

  for (const checkbox of checkboxes) {
    if (!checkbox.checked) continue;

    const dataQuantity = parseInt(checkbox.dataset.quantity, 10);
    if (dataQuantity <= 1) continue;

    const { productkey } = checkbox.dataset;

    console.warn(`[BundleGuard] Addon ${productkey} has data-quantity ${dataQuantity}, correcting to 1`);

    try {
      await fetch("/cart/change.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: productkey, quantity: 0 }),
      })

      // Uncheck and click to re-add cleanly at qty 1
      checkbox.checked = false;
      checkbox.click();

    } catch (err) {
      console.error("[BundleGuard] Failed to correct addon quantity:", err);
    }
  }

  const cart = await fetch("/cart.js").then((r) => r.json());

  const subProducts = cart.items.filter((item) =>
    item.properties && (
      item.properties["_sub_product"] !== undefined ||
      item.properties["_blongs_to_main_product_key"] !== undefined
    )
  );

  if (!subProducts.length) return;

  // Batch all sub-product removals into one request
  const updates = {};
  subProducts.forEach(item => {
    console.warn(`[BundleGuard] Sub-product found: ${item.key}, setting to 0`);
    updates[item.key] = 0;
  });

  try {
    const updatedCart = await fetch("/cart/update.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ updates }),
    }).then((response) => response.json());

    document.dispatchEvent(
      new CustomEvent("cart:update", {
        bubbles: true,
        detail: {
          resource: updatedCart,
          sourceId: "BundleGuard",
          data: {
            source: "BundleGuard",
            itemCount: updatedCart.item_count,
          },
        },
      })
    );
  } catch (err) {
    console.error("[BundleGuard] Failed to remove sub-products:", err);
  }
}